// src/components/admin/ToolManagement.jsx
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ExternalLink, Pencil, Plus, Trash2, Wrench } from 'lucide-react';
import { useTools } from '../../hooks/useTools';
import { useCategories } from '../../hooks/useCategories';
import { findCategory } from '../../config/categories';
import ToolIcon from '../common/ToolIcon';
import { tileColor } from '../../services/toolIcons';
import ToolForm from './ToolForm';
import {
  Button,
  Cell,
  EmptyState,
  ErrorState,
  IconButton,
  Modal,
  PageHeader,
  Panel,
  Pill,
  Row,
  RowActions,
  SearchInput,
  Spinner,
  Table,
} from '../common/ui';

const ToolManagement = () => {
  const { tools, isLoading, error, refetch, createTool, updateTool, deleteTool } = useTools(true);
  const { categories } = useCategories();
  const [editing, setEditing] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tools;
    return tools.filter((tool) =>
      [tool.name, tool.description, tool.id, ...(tool.tags || [])]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [tools, query]);

  const handleSubmit = async (data) => {
    setIsSaving(true);
    try {
      if (editing._id) {
        await updateTool({ toolId: editing._id, toolData: data });
        toast.success(`${data.name} updated`);
      } else {
        await createTool(data);
        toast.success(`${data.name} added`);
      }
      setEditing(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (tool) => {
    if (!window.confirm(`Delete "${tool.name}"? Anyone who starred it will lose it too.`)) return;
    try {
      await deleteTool(tool._id);
      toast.success(`${tool.name} deleted`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete that tool');
    }
  };

  return (
    <div>
      <PageHeader
        title="Tools"
        subtitle="Add a tool once, then choose which roles can see it."
      >
        <Button variant="primary" onClick={() => setEditing({})}>
          <Plus className="h-4 w-4" />
          Add tool
        </Button>
      </PageHeader>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search tools…"
          aria-label="Search tools"
          className="max-w-xs"
        />
        <span className="tnum font-mono text-xs text-ink-3">
          {filtered.length} of {tools.length}
        </span>
      </div>

      {isLoading ? (
        <Spinner label="Loading tools…" />
      ) : error ? (
        <ErrorState title="Couldn't load tools" onRetry={refetch}>
          {error.message}
        </ErrorState>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Wrench}
          title={query ? `No tool matches “${query.trim()}”` : 'No tools yet'}
          action={
            !query && (
              <Button variant="primary" onClick={() => setEditing({})}>
                <Plus className="h-4 w-4" />
                Add the first one
              </Button>
            )
          }
        >
          {query
            ? 'Try a shorter word.'
            : 'Tools you add here show up on everyone’s catalog, filtered by role.'}
        </EmptyState>
      ) : (
        <Panel>
          <Table head={['Tool', 'Category', 'Who can see it', 'Destination', '']}>
            {filtered.map((tool) => {
              const category = findCategory(categories, tool.category);
              return (
                <Row key={tool._id}>
                  <Cell>
                    <div className="flex items-center gap-3">
                      <span className="cat-tile h-9 w-9" data-color={tileColor(tool, category)}>
                        <ToolIcon tool={tool} fill />
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium">{tool.name}</p>
                        <p className="truncate font-mono text-xs text-ink-3">{tool.id}</p>
                      </div>
                    </div>
                  </Cell>

                  <Cell>
                    <span className="flex items-center gap-2 whitespace-nowrap text-[13px] text-ink-2">
                      <span className="cat-dot" data-color={category.color} />
                      {category.label}
                    </span>
                  </Cell>

                  <Cell>
                    <div className="flex flex-wrap gap-1.5">
                      {tool.allowedRoles.map((role) => (
                        <Pill
                          key={role._id}
                          tone={role.name === 'admin' ? 'accent' : 'neutral'}
                          className="capitalize"
                        >
                          {role.name}
                        </Pill>
                      ))}
                    </div>
                  </Cell>

                  <Cell>
                    <a
                      href={tool.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex max-w-[240px] items-center gap-1.5 font-mono text-xs text-ink-2 hover:text-accent"
                    >
                      <ExternalLink className="h-3.5 w-3.5 flex-none text-ink-3" />
                      <span className="truncate">{tool.url.replace(/^https?:\/\//, '')}</span>
                    </a>
                  </Cell>

                  <Cell align="right">
                    <RowActions>
                      <IconButton label={`Edit ${tool.name}`} onClick={() => setEditing(tool)}>
                        <Pencil className="h-4 w-4" />
                      </IconButton>
                      <IconButton
                        label={`Delete ${tool.name}`}
                        tone="danger"
                        onClick={() => handleDelete(tool)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </IconButton>
                    </RowActions>
                  </Cell>
                </Row>
              );
            })}
          </Table>
        </Panel>
      )}

      {editing && (
        <Modal
          size="lg"
          title={editing._id ? `Edit ${editing.name}` : 'Add a tool'}
          description={
            editing._id
              ? 'Changes show up on everyone’s catalog straight away.'
              : 'It appears for anyone whose role you tick below.'
          }
          onClose={() => setEditing(null)}
        >
          <ToolForm
            tool={editing}
            isSaving={isSaving}
            onSubmit={handleSubmit}
            onCancel={() => setEditing(null)}
          />
        </Modal>
      )}
    </div>
  );
};

export default ToolManagement;
